import { type V2, TAU, angleTo, dist, smoothstep } from '../core/math';
import { pointInPolygon } from '../sim/navGrid';
import { AGENT_RADIUS, fbm2, polylineDistance } from './build';
import type { Collider, InteractableDef, PropInstance } from './types';
import type { ZoneBuilder } from './ZoneBuilder';

/**
 * Authoring kit on top of ZoneBuilder: building-kit footprints, walls with
 * gates and arches, street rows and block fills, lamp lines, cliff ridges,
 * edge mountains, ground painting, and a clearance test so collectibles never
 * land inside a collider. Everything draws randomness from the builder's
 * seeded Rng, so layouts stay deterministic.
 *
 * Model conventions: a prop faces local +Z (its door/front), its width runs
 * along local X. With `rot = a` the front faces `(sin a, cos a)`, so
 * `rot = angleTo(prop, street)` makes a building face the street.
 */

/** Unscaled collider footprints of the town/field kit (metres). */
export const FOOTPRINTS: Readonly<Record<string, Collider>> = {
  house_a: { w: 7, d: 6 },
  house_b: { w: 6, d: 5.5 },
  house_c: { w: 9, d: 7 },
  tower: { r: 2.8 }, // = TOWER_R
  market_stall: { w: 3, d: 2 },
  fountain: { r: 2.6 },
  well: { r: 1 },
  forge_anvil: { w: 1.2, d: 0.8 },
  statue_hero: { r: 1.2 },
  bench: { w: 1.8, d: 0.6 },
  banner_pole: { r: 0.3 },
  lamp_post: { r: 0.25 },
  barrel: { r: 0.45 },
  crate: { w: 0.9, d: 0.9 },
  cart: { w: 2.2, d: 1.4 },
  notice_board: { w: 2, d: 0.5 },
  tent: { w: 3, d: 3 },
  campfire: { r: 0.6 },
  signpost: { r: 0.3 },
  pillar_ruin: { r: 0.8 },
  pillar_intact: { r: 0.8 },
  arch: { w: 6, d: 1.2 },
  dungeon_wall: { w: 6, d: 1.5 },
  brazier: { r: 0.5 },
  sarcophagus: { w: 1.2, d: 2.4 },
  altar: { w: 2, d: 1.2 },
  chest_closed: { w: 1, d: 0.7 },
  portal_frame: { w: 5, d: 1.2 },
  gate_iron: { w: 5, d: 0.6 },
  rift_core: { r: 1 },
  ship_player: { w: 5, d: 16 },
  cliff_chunk: { r: 2.2 },
  rock_a: { r: 0.9 },
  rock_b: { r: 0.9 },
  rock_c: { r: 1.2 },
  tree_oak: { r: 0.7 },
  tree_pine: { r: 0.6 },
  tree_pine_snow: { r: 0.6 },
  tree_dead: { r: 0.5 },
  tree_palm: { r: 0.5 },
  cactus: { r: 0.5 },
  ice_crystal: { r: 0.7 },
  log: { w: 2.5, d: 0.7 },
  stump: { r: 0.5 },
};

export const WALL_LEN = 6;
export const WALL_THICK = 1.4;
export const TOWER_R = 2.8;

export interface PlaceOpts {
  scale?: number;
  tint?: number;
  y?: number;
  /** Override the kit footprint; `null` = no collider (walk-through decor). */
  collider?: Collider | null;
  noShadow?: boolean;
  /** Keep scatter away from the footprint (default true when it collides). */
  reserve?: boolean;
}

export function colliderRadius(c: Collider): number {
  return 'r' in c ? c.r : Math.hypot(c.w, c.d) / 2;
}

/** Places a kit prop with its standard collider. */
export function place(b: ZoneBuilder, model: string, x: number, z: number, rot = 0, o: PlaceOpts = {}): void {
  const collider = o.collider === null ? undefined : (o.collider ?? FOOTPRINTS[model]);
  const p: Omit<PropInstance, 'model' | 'x' | 'z'> = { rot };
  if (o.scale !== undefined) p.scale = o.scale;
  if (o.tint !== undefined) p.tint = o.tint;
  if (o.y !== undefined) p.y = o.y;
  if (collider) p.collider = collider;
  if (o.noShadow) p.noShadow = true;
  b.prop(model, x, z, p);
  if (collider && o.reserve !== false) b.reserve({ x, z }, colliderRadius(collider) * (o.scale ?? 1) + 0.8);
}

/** Places a prop at `p` facing the point `toward`. */
export function placeFacing(b: ZoneBuilder, model: string, p: V2, toward: V2, o: PlaceOpts = {}): void {
  place(b, model, p.x, p.z, angleTo(p, toward), o);
}

// ------------------------------------------------------------ polylines

export interface Station {
  p: V2;
  /** Travel direction as a facing angle. */
  angle: number;
  /** Arc length from the start. */
  t: number;
}

export function polylineLength(pts: readonly V2[]): number {
  let l = 0;
  for (let i = 0; i < pts.length - 1; i++) l += dist(pts[i] as V2, pts[i + 1] as V2);
  return l;
}

/** Points every `spacing` metres along a polyline between arc lengths `start` and `end`. */
export function stations(pts: readonly V2[], spacing: number, start = 0, end = Infinity): Station[] {
  const out: Station[] = [];
  let acc = 0;
  let next = start;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i] as V2;
    const c = pts[i + 1] as V2;
    const len = dist(a, c);
    const angle = Math.atan2(c.x - a.x, c.z - a.z);
    while (next <= acc + len && next <= end) {
      const k = len > 0 ? (next - acc) / len : 0;
      out.push({ p: { x: a.x + (c.x - a.x) * k, z: a.z + (c.z - a.z) * k }, angle, t: next });
      next += spacing;
    }
    acc += len;
  }
  return out;
}

/** Offsets `p` sideways from a travel direction: positive = left of travel (screen: north of an eastward road). */
export function sideOffset(p: V2, angle: number, d: number): V2 {
  return { x: p.x + Math.cos(angle) * d, z: p.z - Math.sin(angle) * d };
}

/** Point at arc length `t` along a polyline. */
export function pointAt(pts: readonly V2[], t: number): Station {
  const s = stations(pts, 1e9, Math.max(0, Math.min(t, polylineLength(pts))));
  return s[0] ?? { p: { ...(pts[0] as V2) }, angle: 0, t: 0 };
}

export function closestOnPolyline(p: V2, pts: readonly V2[]): V2 {
  let best: V2 = pts[0] as V2;
  let bestD = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i] as V2;
    const c = pts[i + 1] as V2;
    const abx = c.x - a.x;
    const abz = c.z - a.z;
    const l2 = abx * abx + abz * abz;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.z - a.z) * abz) / l2)) : 0;
    const q = { x: a.x + abx * t, z: a.z + abz * t };
    const d = dist(p, q);
    if (d < bestD) {
      bestD = d;
      best = q;
    }
  }
  return best;
}

// ------------------------------------------------------------ walls & gates

export interface WallOpts {
  model?: string;
  segLen?: number;
  thick?: number;
  /** Openings (gates, harbours): centre point and total width to leave out. */
  gaps?: { p: V2; w: number }[];
  /** Tower model at the polyline vertices (null = none). */
  tower?: string | null;
  towerScale?: number;
  /** Extra towers every N metres along long runs (0 = off). */
  towerEvery?: number;
  tint?: number;
  /** Skip towers at the first/last vertex (walls running into water). */
  openEnds?: boolean;
  /** Base scale of the wall pieces (0.5 = a low garden/graveyard wall). */
  scale?: number;
}

function subtractSpan(spans: [number, number][], s0: number, s1: number): [number, number][] {
  const out: [number, number][] = [];
  for (const [a, c] of spans) {
    if (s1 <= a || s0 >= c) out.push([a, c]);
    else {
      if (s0 > a) out.push([a, s0]);
      if (s1 < c) out.push([s1, c]);
    }
  }
  return out;
}

/**
 * Wall pieces along a polyline (one continuous collider per run so nothing
 * slips between pieces), towers at the corners, gaps left for gates.
 */
export function wall(b: ZoneBuilder, pts: readonly V2[], o: WallOpts = {}): void {
  const model = o.model ?? 'wall_segment';
  const base = o.scale ?? 1;
  const segLen = (o.segLen ?? WALL_LEN) * base;
  const thick = (o.thick ?? WALL_THICK) * base;
  const towerModel = o.tower === undefined ? 'tower' : o.tower;
  const towerScale = o.towerScale ?? 1;
  const towerBaseR = towerModel ? colliderRadius(FOOTPRINTS[towerModel] ?? { r: TOWER_R }) : 0;
  const towerR = towerBaseR * towerScale;
  const towerAt: V2[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i] as V2;
    const c = pts[i + 1] as V2;
    const len = dist(a, c);
    if (len < 0.1) continue;
    const dx = (c.x - a.x) / len;
    const dz = (c.z - a.z) / len;
    const rot = Math.atan2(dx, dz) - Math.PI / 2;
    let spans: [number, number][] = [[0, len]];
    for (const g of o.gaps ?? []) {
      const t = (g.p.x - a.x) * dx + (g.p.z - a.z) * dz;
      const off = Math.abs((g.p.x - a.x) * dz - (g.p.z - a.z) * dx);
      if (off > thick * 3 || t < -g.w / 2 || t > len + g.w / 2) continue;
      spans = subtractSpan(spans, t - g.w / 2, t + g.w / 2);
    }
    for (const [s0, s1] of spans) {
      const run = s1 - s0;
      if (run < 0.4) continue;
      const mid = (s0 + s1) / 2;
      b.blockers.rects.push({ x: a.x + dx * mid, z: a.z + dz * mid, hw: run / 2, hd: thick / 2, rot });
      const n = Math.max(1, Math.round(run / segLen));
      const piece = run / n;
      const s = base * Math.min(1.3, Math.max(0.6, piece / segLen));
      for (let k = 0; k < n; k++) {
        const m = s0 + piece * (k + 0.5);
        const p = { x: a.x + dx * m, z: a.z + dz * m };
        const inst: Omit<PropInstance, 'model' | 'x' | 'z'> = { rot, scale: s };
        if (o.tint !== undefined) inst.tint = o.tint;
        b.prop(model, p.x, p.z, inst);
      }
      for (let m = 0; m <= run; m += 3) b.reserve({ x: a.x + dx * (s0 + m), z: a.z + dz * (s0 + m) }, thick + 1.5);
      if (o.towerEvery && o.towerEvery > 0 && run > o.towerEvery * 1.4) {
        const count = Math.floor(run / o.towerEvery);
        const step = run / (count + 1);
        for (let k = 1; k <= count; k++) towerAt.push({ x: a.x + dx * (s0 + step * k), z: a.z + dz * (s0 + step * k) });
      }
    }
  }
  if (towerModel) {
    const closed = pts.length > 2 && dist(pts[0] as V2, pts[pts.length - 1] as V2) < 0.1;
    pts.forEach((p, i) => {
      if (o.openEnds && (i === 0 || i === pts.length - 1)) return;
      if (closed && i === pts.length - 1) return;
      towerAt.push(p);
    });
    for (const p of towerAt) {
      const inst: Omit<PropInstance, 'model' | 'x' | 'z'> = { rot: 0, scale: towerScale, collider: { r: towerBaseR } };
      if (o.tint !== undefined) inst.tint = o.tint;
      b.prop(towerModel, p.x, p.z, inst);
      b.reserve(p, towerR + 1.5);
    }
  }
}

export interface GateOpts {
  model?: string;
  /** Clear walking width. */
  open?: number;
  /** Total width of the gate model. */
  width?: number;
  thick?: number;
  /** Flanking towers. */
  towers?: string | null;
  towerScale?: number;
  tint?: number;
}

/**
 * A walk-through gate centred on `p` whose local X runs along the wall
 * (`rot` = wall direction angle − π/2, i.e. 0 for an east–west wall). Returns
 * the gap to leave in the wall.
 */
export function gate(b: ZoneBuilder, p: V2, rot: number, o: GateOpts = {}): { p: V2; w: number } {
  const open = o.open ?? 5;
  const width = o.width ?? 9;
  const thick = o.thick ?? 2;
  const ax = { x: Math.cos(rot), z: -Math.sin(rot) };
  const inst: Omit<PropInstance, 'model' | 'x' | 'z'> = { rot };
  if (o.tint !== undefined) inst.tint = o.tint;
  b.prop(o.model ?? 'wall_gate', p.x, p.z, inst);
  const postHalf = (width - open) / 4;
  for (const side of [-1, 1]) {
    const off = open / 2 + postHalf;
    b.blockers.rects.push({ x: p.x + ax.x * off * side, z: p.z + ax.z * off * side, hw: postHalf, hd: thick / 2, rot });
  }
  let total = width;
  const towerModel = o.towers === undefined ? 'tower' : o.towers;
  if (towerModel) {
    const ts = o.towerScale ?? 1;
    const tr = TOWER_R * ts;
    for (const side of [-1, 1]) {
      const off = width / 2 + tr * 0.8;
      const tp = { x: p.x + ax.x * off * side, z: p.z + ax.z * off * side };
      const ti: Omit<PropInstance, 'model' | 'x' | 'z'> = { rot, scale: ts, collider: { r: tr / ts } };
      if (o.tint !== undefined) ti.tint = o.tint;
      b.prop(towerModel, tp.x, tp.z, ti);
    }
    total = width + tr * 3.2;
  }
  b.reserve(p, width / 2 + 2);
  return { p, w: total };
}

/** A walk-through arch (the kit arch is 6 m wide): model without a collider plus two post colliders. */
export function archway(b: ZoneBuilder, p: V2, rot: number, o: { model?: string; open?: number; post?: number; scale?: number; tint?: number } = {}): void {
  const s = o.scale ?? 1;
  const open = (o.open ?? 3.6) * s;
  const post = (o.post ?? 0.6) * s;
  const inst: Omit<PropInstance, 'model' | 'x' | 'z'> = { rot, scale: s };
  if (o.tint !== undefined) inst.tint = o.tint;
  b.prop(o.model ?? 'arch', p.x, p.z, inst);
  const ax = { x: Math.cos(rot), z: -Math.sin(rot) };
  for (const side of [-1, 1]) b.blockers.circles.push({ x: p.x + ax.x * (open / 2 + post) * side, z: p.z + ax.z * (open / 2 + post) * side, r: post });
  b.reserve(p, open / 2 + post * 2);
}

// ------------------------------------------------------------ street furniture

export interface LampOpts {
  model?: string;
  /** Alternate sides instead of lining both. */
  stagger?: boolean;
  start?: number;
  end?: number;
  collider?: Collider;
}

/** Lamp posts along a street (the warm, lit critical path). */
export function lampsAlong(b: ZoneBuilder, pts: readonly V2[], spacing: number, offset: number, o: LampOpts = {}): void {
  const model = o.model ?? 'lamp_post';
  const col = o.collider ?? FOOTPRINTS[model] ?? { r: 0.3 };
  let i = 0;
  for (const s of stations(pts, spacing, o.start ?? spacing / 2, o.end)) {
    const sides = o.stagger ? [i % 2 === 0 ? 1 : -1] : [1, -1];
    for (const side of sides) {
      const p = sideOffset(s.p, s.angle, offset * side);
      if (!isOpen(b, p, 0.3)) continue;
      b.prop(model, p.x, p.z, { rot: s.angle, collider: col });
    }
    i++;
  }
}

export interface RowOpts {
  /** +1 = left of the travel direction, −1 = right. */
  side: 1 | -1;
  /** Street centreline → building centre. */
  setback: number;
  spacing: number;
  models: readonly string[];
  tints?: readonly number[];
  start?: number;
  end?: number;
  scale?: [number, number];
  jitter?: number;
}

/** A row of buildings along one side of a street, each facing it. */
export function rowAlong(b: ZoneBuilder, pts: readonly V2[], o: RowOpts): void {
  for (const s of stations(pts, o.spacing, o.start ?? o.spacing / 2, o.end)) {
    const model = b.rng.pick(o.models);
    const scale = o.scale ? b.rng.range(o.scale[0], o.scale[1]) : 1;
    const tint = o.tints ? b.rng.pick(o.tints) : undefined;
    const jit = o.jitter ? b.rng.range(-o.jitter, o.jitter) : 0;
    const fp = FOOTPRINTS[model] ?? { w: 6, d: 6 };
    const depth = 'r' in fp ? fp.r : fp.d / 2;
    const p = sideOffset(s.p, s.angle, o.side * (o.setback + jit));
    const rad = colliderRadius(fp) * scale;
    if (b.isReserved(p, depth * scale) || !isOpen(b, p, rad)) continue;
    place(b, model, p.x, p.z, angleTo(p, s.p), { scale, tint });
  }
}

export interface FillOpts {
  spacing: number;
  models: readonly string[];
  tints?: readonly number[];
  jitter?: number;
  scale?: [number, number];
  /** Buildings turn to face the nearest of these streets. */
  streets: readonly (readonly V2[])[];
  accept?: (p: V2) => boolean;
}

/** Fills a block with buildings on a jittered grid (skipping streets, plazas and colliders). */
export function fillBuildings(b: ZoneBuilder, area: { minX: number; minZ: number; maxX: number; maxZ: number }, o: FillOpts): void {
  for (let z = area.minZ; z <= area.maxZ; z += o.spacing) {
    for (let x = area.minX; x <= area.maxX; x += o.spacing) {
      const j = o.jitter ?? 0;
      const p = { x: x + b.rng.range(-j, j), z: z + b.rng.range(-j, j) };
      const model = b.rng.pick(o.models);
      const scale = o.scale ? b.rng.range(o.scale[0], o.scale[1]) : 1;
      const tint = o.tints ? b.rng.pick(o.tints) : undefined;
      const fp = FOOTPRINTS[model] ?? { w: 6, d: 6 };
      const rad = colliderRadius(fp) * scale;
      if (o.accept && !o.accept(p)) continue;
      if (b.isReserved(p, rad * 0.8) || !isOpen(b, p, rad)) continue;
      let best: V2 | null = null;
      let bestD = Infinity;
      for (const st of o.streets) {
        const q = closestOnPolyline(p, st);
        const d = dist(p, q);
        if (d < bestD) {
          bestD = d;
          best = q;
        }
      }
      const facing = best && bestD < 40 ? angleTo(p, best) : 0;
      // Snap to the nearest 45° so blocks read as planned streets.
      const rot = Math.round(facing / (Math.PI / 4)) * (Math.PI / 4);
      place(b, model, p.x, p.z, rot, { scale, tint });
    }
  }
}

export interface RingOpts {
  phase?: number;
  scale?: number;
  tint?: number;
  collider?: Collider | null;
  /** Rotate each prop to face the centre. */
  faceIn?: boolean;
  /** Skip props whose angle (compass, 0 = south/+Z) returns true. */
  skip?: (a: number) => boolean;
  y?: number;
}

/** `n` props on a circle around `c`. */
export function ringOf(b: ZoneBuilder, model: string, c: V2, r: number, n: number, o: RingOpts = {}): void {
  for (let i = 0; i < n; i++) {
    const a = (o.phase ?? 0) + (i / n) * TAU;
    if (o.skip?.(a)) continue;
    const p = { x: c.x + Math.sin(a) * r, z: c.z + Math.cos(a) * r };
    const opts: PlaceOpts = { scale: o.scale, tint: o.tint, y: o.y };
    if (o.collider !== undefined) opts.collider = o.collider;
    place(b, model, p.x, p.z, o.faceIn ? a + Math.PI : a, opts);
  }
}

// ------------------------------------------------------------ terrain features

export interface RidgeOpts {
  /** Width of the impassable core. */
  width: number;
  height: number;
  /** Width of the slope on each side. */
  soft?: number;
  models?: { model: string; weight: number; scale?: [number, number] }[];
  /** Spacing of cliff props along each face (0 = none). */
  spacing?: number;
  seed?: number;
  tint?: number;
  /** Ground layer painted over the top. */
  layer?: number;
}

/**
 * A raised, impassable band of rock (mesa rim, canyon wall, ridge): terrain
 * bump, collider along the spine and cliff props dressing both faces.
 */
export function ridge(b: ZoneBuilder, pts: readonly V2[], o: RidgeOpts): void {
  const hw = o.width / 2;
  const soft = o.soft ?? 5;
  const seed = o.seed ?? 7;
  const pad = hw + soft + 1;
  const bx0 = Math.min(...pts.map((p) => p.x)) - pad;
  const bx1 = Math.max(...pts.map((p) => p.x)) + pad;
  const bz0 = Math.min(...pts.map((p) => p.z)) - pad;
  const bz1 = Math.max(...pts.map((p) => p.z)) + pad;
  const k = (x: number, z: number): number => {
    if (x < bx0 || x > bx1 || z < bz0 || z > bz1) return 0;
    const d = polylineDistance({ x, z }, pts);
    if (d >= hw + soft) return 0;
    return 1 - smoothstep(hw - 1, hw + soft, d);
  };
  b.addHeight((x, z) => {
    const kk = k(x, z);
    if (kk <= 0) return 0;
    return o.height * kk * (0.8 + 0.4 * fbm2(x * 0.07, z * 0.07, seed, 2));
  });
  if (o.layer !== undefined) {
    const layer = o.layer;
    b.addSurface((x, z, w) => {
      const kk = k(x, z);
      if (kk > 0.4) w[layer] = (w[layer] ?? 0) + (kk - 0.4) * 5;
    });
  }
  const block = hw + soft * 0.3;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i] as V2;
    const c = pts[i + 1] as V2;
    const len = dist(a, c);
    b.blockers.rects.push({ x: (a.x + c.x) / 2, z: (a.z + c.z) / 2, hw: len / 2, hd: block, rot: Math.atan2(c.x - a.x, c.z - a.z) - Math.PI / 2 });
  }
  for (const p of pts) b.blockers.circles.push({ x: p.x, z: p.z, r: block });
  for (const s of stations(pts, Math.max(2, hw))) b.reserve(s.p, hw + soft * 0.6);
  const spacing = o.spacing ?? 5;
  const models = o.models ?? [
    { model: 'cliff_chunk', weight: 3, scale: [1.2, 2] },
    { model: 'rock_c', weight: 1, scale: [1, 1.8] },
  ];
  if (spacing <= 0) return;
  for (const s of stations(pts, spacing, spacing / 2)) {
    for (const side of [-1, 1]) {
      const off = hw + soft * 0.25 + b.rng.range(-0.8, 0.8);
      const p = sideOffset(s.p, s.angle, off * side);
      const m = b.rng.weighted(models.map((x) => ({ weight: x.weight, value: x })));
      const scale = m.scale ? b.rng.range(m.scale[0], m.scale[1]) : 1;
      const inst: Omit<PropInstance, 'model' | 'x' | 'z'> = { rot: b.rng.range(0, TAU), scale, collider: { r: colliderRadius(FOOTPRINTS[m.model] ?? { r: 1 }) * 0.8 } };
      if (o.tint !== undefined) inst.tint = o.tint;
      b.prop(m.model, p.x, p.z, inst);
    }
  }
  // Caps at both ends.
  for (const e of [pts[0] as V2, pts[pts.length - 1] as V2]) {
    const m = models[0] as { model: string; scale?: [number, number] };
    const scale = m.scale ? m.scale[1] : 1.5;
    const inst: Omit<PropInstance, 'model' | 'x' | 'z'> = { rot: b.rng.range(0, TAU), scale, collider: { r: colliderRadius(FOOTPRINTS[m.model] ?? { r: 1 }) * 0.8 } };
    if (o.tint !== undefined) inst.tint = o.tint;
    b.prop(m.model, e.x, e.z, inst);
  }
}

/**
 * Mountains/dunes rising toward the map edge, cut down around the exits so
 * portals sit in valley mouths.
 */
export function edgeRise(b: ZoneBuilder, depth: number, height: number, gaps: readonly V2[], gapRadius = 18, seed = 3): void {
  b.addHeight((x, z) => {
    const d = Math.min(x - b.minX, b.maxX - x, z - b.minZ, b.maxZ - z);
    if (d >= depth) return 0;
    let k = 1 - smoothstep(0, depth, d);
    for (const g of gaps) {
      const gd = dist({ x, z }, g);
      if (gd < gapRadius) k *= smoothstep(gapRadius * 0.35, gapRadius, gd);
    }
    return height * k * k * (0.6 + 0.8 * fbm2(x * 0.035, z * 0.035, seed, 3));
  });
}

/** Adds weight to one ground layer wherever `f` returns > 0 (0..1). */
export function paint(b: ZoneBuilder, layer: number, f: (x: number, z: number) => number, strength = 4): void {
  b.addSurface((x, z, w) => {
    const k = f(x, z);
    if (k > 0) w[layer] = (w[layer] ?? 0) + k * strength;
  });
}

/** 1 inside radius `r` of `c`, fading to 0 over `soft` metres. */
export const disc =
  (c: V2, r: number, soft = 3) =>
  (x: number, z: number): number => {
    const d = Math.hypot(x - c.x, z - c.z);
    return d >= r + soft ? 0 : 1 - smoothstep(r, r + soft, d);
  };

// ------------------------------------------------------------ clearance

function inRect(p: V2, cx: number, cz: number, hw: number, hd: number, rot: number): boolean {
  const cos = Math.cos(-rot);
  const sin = Math.sin(-rot);
  const dx = p.x - cx;
  const dz = p.z - cz;
  const lx = dx * cos + dz * sin;
  const lz = -dx * sin + dz * cos;
  return Math.abs(lx) <= hw && Math.abs(lz) <= hd;
}

/**
 * True when an agent standing at `p` (plus `pad` metres of elbow room) would
 * be on open ground: inside the walkable area, above deep water and clear of
 * every collider and blocker placed so far.
 */
export function isOpen(b: ZoneBuilder, p: V2, pad = 0.5): boolean {
  const r = AGENT_RADIUS + pad;
  const m = 1.5 + pad;
  if (p.x < b.minX + m || p.x > b.maxX - m || p.z < b.minZ + m || p.z > b.maxZ - m) return false;
  const w = b.walkable;
  if (w?.kind === 'circle' && dist(p, w) > w.r - r) return false;
  if (w?.kind === 'poly' && !pointInPolygon(p, w.pts)) return false;
  for (const wr of b.water) {
    if (!wr.deep) continue;
    const s = wr.shape;
    if (s.kind === 'sheet') {
      const lim = wr.level - 0.35 + 0.15;
      if (b.heightAt(p.x, p.z) < lim || b.heightAt(p.x + r, p.z) < lim || b.heightAt(p.x - r, p.z) < lim || b.heightAt(p.x, p.z + r) < lim || b.heightAt(p.x, p.z - r) < lim) return false;
    } else if (s.kind === 'rect') {
      if (Math.abs(p.x - s.x) < s.w / 2 + r && Math.abs(p.z - s.z) < s.d / 2 + r) return false;
    } else if (dist(p, s) < s.r + r) return false;
  }
  for (const pr of b.props) {
    const c = pr.collider;
    if (!c) continue;
    const s = pr.scale ?? 1;
    if ('r' in c) {
      if (dist(p, pr) < c.r * s + r) return false;
    } else if (inRect(p, pr.x, pr.z, (c.w * s) / 2 + r, (c.d * s) / 2 + r, pr.rot ?? 0)) return false;
  }
  for (const c of b.blockers.circles) if (dist(p, c) < c.r + r) return false;
  for (const rc of b.blockers.rects) if (inRect(p, rc.x, rc.z, rc.hw + r, rc.hd + r, rc.rot ?? 0)) return false;
  for (const poly of b.blockers.polys) if (pointInPolygon(p, poly)) return false;
  return true;
}

export interface Area {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

/**
 * Random open spots (clear of colliders, water and reserved features). They
 * also keep 5 m from NPCs, whose talk prompt would otherwise win the
 * interact key, and 3 m from other interactables.
 */
export function openSpots(b: ZoneBuilder, count: number, area: Area, minDist: number, accept?: (p: V2) => boolean): V2[] {
  const out: V2[] = [];
  const tries = count * 60;
  for (let i = 0; i < tries && out.length < count; i++) {
    const p = { x: b.rng.range(area.minX, area.maxX), z: b.rng.range(area.minZ, area.maxZ) };
    if (accept && !accept(p)) continue;
    if (out.some((q) => dist(p, q) < minDist)) continue;
    if (b.npcs.some((n) => dist(n, p) < 5) || b.interactables.some((it) => dist(it, p) < 3)) continue;
    if (b.isReserved(p, 1) || !isOpen(b, p, 0.8)) continue;
    out.push(p);
  }
  return out;
}

export interface NodeSpec {
  kind: 'herb' | 'timber' | 'ore' | 'dig' | 'seed';
  count: number;
  area: Area;
  minDist: number;
  accept?: (p: V2) => boolean;
}

const NODE_DATA: Record<NodeSpec['kind'], { kind: InteractableDef['kind']; data?: InteractableDef['data'] }> = {
  herb: { kind: 'gather', data: { skill: 'foraging', node: 'herb' } },
  timber: { kind: 'gather', data: { skill: 'logging', node: 'timber' } },
  ore: { kind: 'gather', data: { skill: 'mining', node: 'ore' } },
  dig: { kind: 'dig', data: { skill: 'excavating' } },
  seed: { kind: 'seed' },
};

/** Life-skill nodes and Glimmerseeds with ids `<prefix>_<kind>_<i>`. */
export function lifeNodes(b: ZoneBuilder, prefix: string, specs: readonly NodeSpec[]): void {
  for (const s of specs) {
    const nd = NODE_DATA[s.kind];
    const tag = `${prefix}_${s.kind}_`;
    const base = b.interactables.filter((i) => i.id.startsWith(tag)).length;
    openSpots(b, s.count, s.area, s.minDist, s.accept).forEach((p, i) => {
      const def: InteractableDef = { id: `${tag}${base + i}`, kind: nd.kind, x: p.x, z: p.z };
      if (nd.data) def.data = { ...nd.data };
      b.interact(def);
    });
  }
}
